import React, { useEffect, useRef, useState } from "react";
import { Suspense } from "react";
import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useSelector } from "react-redux";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";

import Header from "../../components/Header";
import Station from "../Station/Station";
import Quin from "../Quin/Quin";
import Gestock from "../Gestock/Gestock";
import Logs from "../Logs/Logs";
import StationChat from "../Chat/StationChat";
import { db } from "../../config/firebaseConfig";
import { selectActiveUid } from "../Auth/authSlice";

const Dashboard = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [hasUnreadChat, setHasUnreadChat] = useState(false);
  const quin = useSelector((state) => state.auth.quin);
  const station = useSelector((state) => state.auth.station);
  const activeUid = useSelector(selectActiveUid);
  const showGestock = station === "GESTOCK";

  const tabs = [{ label: "Station", element: <Station /> }];
  if (quin) tabs.push({ label: quin, element: <Quin /> });
  tabs.push({ label: "Rapport", element: <Logs /> });
  tabs.push({ label: "Chat", element: <StationChat /> });

  const chatTabIndex = tabs.length - 1;

  // Keep a ref to avoid stale closure in onSnapshot
  const tabIndexRef = useRef(tabIndex);
  useEffect(() => { tabIndexRef.current = tabIndex; }, [tabIndex]);

  // Watch for new incoming messages to highlight the Chat tab
  const initializedRef = useRef(false);
  const knownIdsRef = useRef(new Set());

  useEffect(() => {
    if (!activeUid || showGestock) return;

    initializedRef.current = false;
    knownIdsRef.current = new Set();

    const q = query(
      collection(db, "users", activeUid, "chat_messages"),
      orderBy("ts", "desc"),
      limit(20),
    );

    const unsub = onSnapshot(q, (snap) => {
      if (!initializedRef.current) {
        // First load — record existing IDs, don't mark unread
        snap.docs.forEach((d) => knownIdsRef.current.add(d.id));
        initializedRef.current = true;
        return;
      }

      // Check for new "in" messages not yet seen
      const hasNewIn = snap.docs.some(
        (d) => !knownIdsRef.current.has(d.id) && d.data().direction === "in",
      );

      // Record all current IDs
      snap.docs.forEach((d) => knownIdsRef.current.add(d.id));

      if (hasNewIn && tabIndexRef.current !== chatTabIndex) {
        setHasUnreadChat(true);
      }
    });

    return () => unsub();
  }, [activeUid, showGestock, chatTabIndex]);

  const handleTabChange = (_, idx) => {
    setTabIndex(idx);
    if (idx === chatTabIndex) setHasUnreadChat(false);
  };

  if (showGestock) {
    return <Gestock />;
  }

  return (
    <>
      <Header />
      <Box sx={{ width: "100%", mt: 2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          {tabs.map((tab, idx) => (
            <Tab
              key={tab.label}
              label={tab.label}
              sx={
                tab.label === "Chat" && hasUnreadChat
                  ? { bgcolor: "success.main", color: "white", fontWeight: 700, borderRadius: 1 }
                  : {}
              }
            />
          ))}
        </Tabs>
        <Box sx={{ mt: 2 }}>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            }
          >
            {tabs[tabIndex]?.element || null}
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;
